import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium tracking-wide transition-[background-color,box-shadow,transform,color,border-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        // Primary: skewed cyan gradient with controlled glow
        primary:
          "btn-skew rounded-md bg-[linear-gradient(92deg,#2E7BFF_0%,#1aa3e8_55%,#12d1e6_100%)] font-semibold text-[#05070D] shadow-[0_0_0_1px_rgba(120,190,255,0.45),0_10px_30px_-10px_rgba(46,123,255,0.8),0_0_40px_-16px_rgba(18,209,230,0.6)] hover:-translate-y-px hover:brightness-110 hover:shadow-[0_0_0_1px_rgba(160,215,255,0.7),0_14px_38px_-10px_rgba(46,123,255,0.95),0_0_60px_-14px_rgba(18,209,230,0.8)]",
        // Secondary: dark glass with neon hairline
        secondary:
          "rounded-md border border-[#2E7BFF]/40 bg-[linear-gradient(180deg,rgba(20,30,52,0.7),rgba(9,14,26,0.85))] text-[#9CC8FF] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] hover:border-[#2E7BFF]/80 hover:text-foreground hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_0_20px_-6px_rgba(46,123,255,0.6)] hover:-translate-y-px",
        // Ghost: minimal
        ghost:
          "rounded-md bg-transparent text-foreground/80 hover:bg-[#2E7BFF]/10 hover:text-foreground",
        // Yellow: phosphor CTA (Dashboard only)
        yellow: "cta-yellow",
      },
      size: {
        default: "h-10 px-6 text-sm",
        sm: "h-8 px-4 text-xs",
        lg: "h-12 px-8 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {variant === "primary" ? (
          <span className="inline-flex items-center gap-2">{children}</span>
        ) : (
          children
        )}
      </button>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
